import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BusinessSetupSchema, type BusinessSetup, type BusinessSetupAnswers, type BusinessSetupReply, type BusinessSetupDocument, type BusinessSetupTranscript } from '@tenfour/contracts'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/components/auth/AuthContext'
import { api } from '@/lib/api'
import { BusinessConversation, emptyAnswers } from '../public/outreach-signup/BusinessConversation'
import { SignupConversation } from '../public/outreach-signup/SignupConversation'
import { SignupIntro } from '../public/outreach-signup/visuals'

export default function BusinessSetupPage() {
  usePageTitle()
  const { accountId } = useAuth()
  const query = useQuery({ queryKey: ['business-setup', accountId], queryFn: () => api<{ required: boolean; businessName: string; draft: unknown }>('/business-setup') })
  if (query.isError) return <p role="alert">Setup couldn’t load. <button onClick={() => query.refetch()}>Retry</button></p>
  if (!query.data) return <p role="status">Loading your answers…</p>
  const parsed = BusinessSetupSchema.safeParse(query.data.draft)
  return <SetupSession initial={parsed.success ? parsed.data : { answers: emptyAnswers, deferred: true }} required={query.data.required} />
}
function SetupSession({ initial, required }: { initial: BusinessSetup; required: boolean }) {
  const [draft, setDraft] = useState(initial)
  const [arriving, setArriving] = useState(required)
  const answers = draft.answers
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const cache = useQueryClient()
  async function save(deferred: boolean) {
    setSaving(true); setError('')
    try {
      await api('/business-setup', { method: 'POST', body: JSON.stringify({ ...draft, answers, deferred } satisfies BusinessSetup) })
      await cache.invalidateQueries({ queryKey: ['business-setup'] })
      await cache.invalidateQueries({ queryKey: ['account'] })
      navigate('/home')
    } catch (err) { setError(err instanceof Error ? err.message : 'Your answers couldn’t save. Try again.') }
    finally { setSaving(false) }
  }
  async function updateAnswers(next: BusinessSetupAnswers, projection?: BusinessSetupReply['projection'], transcript?: BusinessSetupTranscript) {
    const updated = { ...draft, transcript: transcript ?? draft.transcript, answers: next, projection: projection ?? draft.projection, deferred: true }
    await api('/business-setup', { method: 'POST', body: JSON.stringify(updated) })
    setDraft(updated)
  }
  async function updateDocuments(documents: BusinessSetupDocument[]) {
    const updated = { ...draft, documents, deferred: true }
    await api('/business-setup', { method: 'POST', body: JSON.stringify(updated) })
    setDraft(updated)
  }
  function finish() { void save(false) }
  function later() { void save(true) }
  function home() { navigate('/home') }
  if (arriving) return <SignupIntro onComplete={() => setArriving(false)} />
  return <SignupConversation stickyTitle={required} title={required ? 'Let’s get to know you.' : 'Your business setup is complete.'} onBack={later}>
    {error && <p role="alert">{error}</p>}
    {required ? <BusinessConversation answers={answers} documents={draft.documents ?? []} transcript={draft.transcript ?? []} onDocumentsChange={updateDocuments} onChange={updateAnswers} onContinue={finish} onSkip={later} saving={saving} /> : <button onClick={home}>Back to Home</button>}
  </SignupConversation>
}
